import { useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { ScrollVFlex, Space } from '@/components/base';
import {
  DASHBOARD_ROW_DEFAULT_HEIGHT,
  Dashboard2ColumnType,
} from '@/features/Dashboard/types/constants';
import type {
  Dashboard2T,
  MappedChartColumn,
  MappedChartRow,
} from '@/features/Dashboard/types/types';
import useDashboard2Store from '@/stores/Dashboard2Store';

import ControlledCreateTextArea from './ControlledCreateTextArea';
import DashboardAddChartCard from './DashboardAddChartCard';
import DashboardRowGroup from './DashboardRowGroup';
import DashboardRowHandler from './DashboardRowHandler';
import PendingFeedbackGalleryRow from './PendingFeedbackGalleryRow';

type DashboardRowListProps = {
  dashboard: Dashboard2T;
};

const DashboardRowList = (props: DashboardRowListProps) => {
  const [someDragging, setSomeDragging] = useState(false);
  const pendingFeedbackGallery = useDashboard2Store(
    (state) => state.pendingFeedbackGallery,
  );

  const rowList: MappedChartRow[] = useMemo(() => {
    return (
      props.dashboard.rows?.map((row) => {
        return {
          ...row,
          columns:
            row.columns?.map((col) => {
              if (
                col.type === Dashboard2ColumnType.DASHBOARD_COLUMN_CHART &&
                col.id &&
                col.chart
              ) {
                const chart = props.dashboard.charts?.find(
                  (chart) => chart.alias === col.chart,
                );
                return {
                  id: col.id,
                  type: col.type,
                  chart: chart,
                };
              } else if (
                col.type === Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS &&
                col.id &&
                col.analysis_id
              ) {
                const analysis = props.dashboard.analyses?.find(
                  (analysis) => analysis.alias === col.analysis_id,
                );
                return {
                  id: col.id,
                  type: col.type,
                  analysis: analysis,
                };
              } else if (
                col.type === Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY &&
                col.id
              ) {
                return {
                  id: col.id,
                  type: col.type,
                  feedbacks_setting: col.feedbacks_setting,
                };
              } else {
                return col as MappedChartColumn;
              }
            }) || [],
        };
      }) || []
    );
  }, [props.dashboard.analyses, props.dashboard.charts, props.dashboard.rows]);

  const currentCharts = useMemo(
    () => props.dashboard.charts?.map((chart) => chart.alias) || [],
    [props.dashboard.charts],
  );

  const renderPendingRow = (rowIndex: number) => {
    if (!pendingFeedbackGallery) return null;
    if (pendingFeedbackGallery.dashboardId !== props.dashboard.alias) return null;
    if (pendingFeedbackGallery.rowIndex !== rowIndex) return null;

    return (
      <PendingFeedbackGalleryRow
        dashboardId={props.dashboard.alias}
        rowIndex={pendingFeedbackGallery.rowIndex}
        feedbacksSetting={pendingFeedbackGallery.feedbacksSetting}
      />
    );
  };

  return (
    <ScrollVFlex height="100%" f={1}>
      <div
        style={{
          padding: '0px 12px 12px 28px',
          overflow: 'auto',
          width: '100%',
          minWidth: '1200px',
          scrollbarGutter: 'stable',
        }}
      >
        {rowList && (
          <DndProvider backend={HTML5Backend}>
            <DashboardRowHandler
              dashboardId={props.dashboard.alias}
              rowIndex={0}
              rowList={rowList}
              someDragging={someDragging}
              currentCharts={currentCharts}
            />
            {renderPendingRow(0)}

            {rowList.map((row, i) => {
              return (
                <div key={row.id}>
                  <DashboardRowGroup
                    row={row}
                    rowIndex={i + 1}
                    dashboardId={props.dashboard.alias}
                    someDragging={someDragging}
                    setSomeDragging={setSomeDragging}
                    rowHeight={(function () {
                      if (row.height) return row.height;
                      const onlyTextBox = row.columns.every(
                        (col) =>
                          col.type ===
                          Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
                      );
                      if (onlyTextBox) return 'auto';
                      return `${DASHBOARD_ROW_DEFAULT_HEIGHT}px`;
                    })()}
                  />

                  <DashboardRowHandler
                    dashboardId={props.dashboard.alias}
                    rowIndex={i + 1}
                    rowList={rowList}
                    someDragging={someDragging}
                    currentCharts={currentCharts}
                    rowId={row.id}
                  />
                  {renderPendingRow(i + 1)}
                </div>
              );
            })}

            <ControlledCreateTextArea
              length={rowList.length}
              dashboardId={props.dashboard.alias}
            />
          </DndProvider>
        )}
        <DashboardAddChartCard
          currentCharts={currentCharts}
          height="345px"
          dashboardId={props.dashboard.alias}
          rowCount={rowList.length}
        />

        <Space h={500} />
      </div>
    </ScrollVFlex>
  );
};

export default DashboardRowList;
